import { ChangeEvent } from 'react'
import { Outlet } from 'react-router-dom'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  TrainingsDataTableQuery,
  useTrainingsDataTable,
} from 'generated/resources/datatable.graphql'
import {
  ParticipantEmployeeFragment,
  TrainingListPageCompetencyFragment,
  TrainingsCategory,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import DataTable, { useDataTable } from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import employees from 'modules/employees/store/collections/employees'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import { ItemsTags } from 'modules/trainings/components/Shared/ItemsTags'
import { StatusTag } from 'modules/trainings/components/TrainingsDataTable/StatusTag'
import { categoriesFilter } from 'modules/trainings/components/TrainingsDataTable/categoriesFilter'
import { competenciesFilter } from 'modules/trainings/components/TrainingsDataTable/competenciesFilter'
import { courseExpirationFilter } from 'modules/trainings/components/TrainingsDataTable/courseExpirationFilter'
import { employeeFilter } from 'modules/trainings/components/TrainingsDataTable/employeeFilter'
import { statusFilter } from 'modules/trainings/components/TrainingsDataTable/statusFilter'
import { yearFilter } from 'modules/trainings/components/TrainingsDataTable/yearFilter'
import { editPolicyKey } from 'modules/trainings/hooks/Trainings/useTrainings'

import { ActionsDropdown } from './ActionsDropdown'
import { Participants } from './Participants'

export const TrainingsSelectableDataTable = ({
  canCreate,
  competencies,
  categories,
  employeesData,
}: {
  canCreate: boolean
  competencies?: TrainingListPageCompetencyFragment[]
  categories?: TrainingsCategory[]
  employeesData: ParticipantEmployeeFragment[]
}) => {
  const statusTemplate = `{% if status == 'active' -%}
    ${i18n.t('trainings.trainings_table.status.active')}
  {%- else -%}
    ${i18n.t('trainings.trainings_table.status.draft')}
  {%- endif -%}`

  const { search, debouncedSearch, onSearch } = useDebouncedSearch('', {
    delay: true,
    minimumCharacters: 3,
  })
  const { navigation } = useNavigation()

  const source = useTrainingsDataTable(
    [
      {
        identifier: 'name',
        kind: ProjectionKind.Path,
        title: i18n.t('trainings.trainings_table.name'),
        path: 'name',
      },
      {
        identifier: 'totalParticipants',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_table.participants'),
        render: ({ totalParticipants, numberOfExpiredParticipants }) => {
          return (
            <Participants
              totalParticipants={totalParticipants}
              numberOfExpiredParticipants={numberOfExpiredParticipants}
            />
          )
        },
        template: '{{totalParticipants}}',
      },
      {
        identifier: 'catalog',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_table.catalog'),
        render: ({ catalog }) => {
          return (
            <Box flexDirection="row" gap="s4" alignItems="center">
              {catalog ? (
                <>
                  <Icon.Small color="grey1000" icon={'eye-visible'} />
                  <Text.Normal weight="medium" color="grey1000">
                    {i18n.t('trainings.on_catalog')}
                  </Text.Normal>
                </>
              ) : (
                '-'
              )}
            </Box>
          )
        },
        template: '',
      },
      {
        identifier: 'status',
        kind: ProjectionKind.Custom,
        render: ({ status }) => {
          if (!status) return

          return <StatusTag status={status} />
        },
        title: i18n.t('trainings.trainings_table.status.status_title'),
        template: statusTemplate,
      },
      {
        identifier: 'tags',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_table.tags'),
        render: ({ categories }) => {
          return <ItemsTags itemsName={categories.map((category) => category.name)} />
        },
        hidden: true,
        template: '{{ categories }}',
      },
      {
        identifier: 'competencies',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_table.competencies'),
        render: ({ competencies }) => {
          return <ItemsTags itemsName={competencies.map((competency) => competency.name)} />
        },
        hidden: true,
        template: '{{ competencies }}',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: ({ id, catalog, status }) => {
          return (
            <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
              <ActionsDropdown id={Number(id)} catalog={catalog} status={status} />
              <Button.Router
                type="secondary"
                to={navigation.trainings.list.details_revamp.view.overview.fullPath({
                  id: String(id),
                })}
                iconName="arrow-right"
              />
            </Box>
          )
        },
      },
    ],
    {
      variables: { search: debouncedSearch },
    }
  )

  const filters = [
    competenciesFilter(source, 'TrainingsCompetenciesFilter', competencies),
    statusFilter(source),
    employeeFilter(source, employeesData),
    yearFilter(source),
    courseExpirationFilter(source),
    categoriesFilter(source, categories),
  ]

  return (
    <DataTable.Provider
      source={source}
      sort={{
        path: 'name',
      }}
      filters={filters}
    >
      <Box gap="s8">
        <Header search={search} onSearch={onSearch} canCreate={canCreate} />
        <Table />
      </Box>
      <Outlet />
    </DataTable.Provider>
  )
}

interface HeaderProps {
  search: string
  onSearch: (event: ChangeEvent<HTMLInputElement>) => void
  canCreate: boolean
}

const Header = ({ canCreate, search, onSearch }: HeaderProps) => {
  const { navigation } = useNavigation()
  return (
    <DataTable.HeadBar
      search={{
        placeholder: i18n.t('common.search'),
        value: search,
        onChange: onSearch,
      }}
      disableExport
      actions={<Actions canCreate={canCreate} />}
      customPopoverOptions={
        <PopoverMenu.RouterOption
          title={i18n.t('trainings.trainings_table.export_name')}
          description={i18n.t('trainings.trainings_table.export_description')}
          to={{ pathname: navigation.trainings.list.index.export.fullPath() }}
        />
      }
    />
  )
}

const Table = () => {
  const data = useDataTable<TrainingsDataTableQuery>()
  const edges = data.queryResult.data?.trainings.trainingsConnection.edges
  const accessIds =
    data.queryResult.data?.trainings.trainingsConnection.edges.map((edge) => edge.node?.authorId) ??
    []
  const targets = accessIds.map((accessId) =>
    employees.find({
      access_id: accessId,
    })
  )

  const targetIds = targets.map((target) => Number(target?.get('id'))).join(',')

  useAsyncData(() => {
    if (!targets.length) return Promise.resolve()

    return policyAuthorizations.authorizePolicies(editPolicyKey, compact(targets))
  }, [targetIds])

  const nonSelectableIds = edges?.map((edge) => {
    const node = edge.node
    if (!node) return

    const employee = targets?.find((target) => target?.get('access_id') === node.authorId)
    if (!employee) return

    if (!policyAuthorizations.for(editPolicyKey, employee)) {
      return +node.id
    }
  })

  return <DataTable.Table selectable={(item) => !nonSelectableIds?.includes(item.id)} />
}

const Actions = ({ canCreate }: { canCreate: boolean }) => {
  const data = useDataTable<TrainingsDataTableQuery>()
  const { navigation } = useNavigation()
  return (
    <Box flexDirection="row" gap="s8">
      <PopoverMenu type="list">
        <PopoverMenu.Trigger asChild disabled={data.selectedRows.size === 0}>
          <Button type="secondary" disabled={data.selectedRows.size === 0}>
            {i18n.t('trainings.trainings_table.manage_visibility')}
          </Button>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="end">
          <PopoverMenu.RouterOption
            title={i18n.t('trainings.trainings_table.display_catalog')}
            to={{
              pathname: navigation.trainings.list.index.bulk_update_catalog_visible.fullPath(),
            }}
          />
          <PopoverMenu.RouterOption
            title={i18n.t('trainings.trainings_table.hide_catalog')}
            to={{ pathname: navigation.trainings.list.index.bulk_update_catalog_remove.fullPath() }}
          />
        </PopoverMenu.Content>
      </PopoverMenu>
      <Button.Router
        type="secondary"
        disabled={data.selectedRows.size === 0}
        to={navigation.trainings.list.index.bulk_delete.fullPath()}
        iconName="delete"
      />
      {canCreate && (
        <Button.Router
          type="primary"
          to={navigation.trainings.list.index.new.fullPath()}
          iconName="add"
          onClick={() => track(trackingEvents.training.trainingCreationWizard.goToNewCourseWizard)}
        >
          {i18n.t('trainings.trainings_table.add')}
        </Button.Router>
      )}
    </Box>
  )
}
